import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/utils.js';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** When true the card is a clickable surface: hover lifts it with a
   *  subtle shadow + brand border. Leave false for static containers. */
  interactive?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-[var(--app-divider)] bg-[var(--n-0)] p-6 shadow-1',
        interactive &&
          'cursor-pointer [transition:box-shadow_var(--dur-hover)_var(--ease-out),border-color_var(--dur-hover)_var(--ease-out)] hover:border-[var(--brand-400)] hover:shadow-2',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mb-4 space-y-1', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn(
        'text-h2 font-sans font-semibold tracking-tight text-[var(--ink-700)]',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-body text-[var(--fg-2)]', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';
